import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  ArrowRightLeft,
  CheckCircle2,
  Lock,
  Network,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Waypoints,
} from 'lucide-react';
import { toast } from '../ui/sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MetricCard = ({ label, value, helper, testId }) => (
  <div className="glass-card tracing-panel p-5" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{label}</p>
    <p className="mt-3 text-3xl font-bold text-text-heading">{value}</p>
    <p className="mt-2 text-xs text-text-muted">{helper}</p>
  </div>
);

const STATUS_STYLES = {
  ready: 'border border-emerald-400/20 bg-emerald-500/10 text-emerald-200',
  connected: 'border border-primary/20 bg-primary/10 text-primary',
  active: 'border border-primary/20 bg-primary/10 text-primary',
  pending: 'border border-amber-400/20 bg-amber-500/10 text-amber-200',
  blocked: 'border border-rose-400/20 bg-rose-500/10 text-rose-200',
};

const GO_LIVE_STATUS_STYLES = {
  ready: 'border border-emerald-400/20 bg-emerald-500/10 text-emerald-200',
  connected: 'border border-primary/20 bg-primary/10 text-primary',
  pending: 'border border-amber-400/20 bg-amber-500/10 text-amber-200',
  blocked: 'border border-rose-400/20 bg-rose-500/10 text-rose-200',
};

const WorkbenchCard = ({ item, activeView, setActiveView }) => (
  <button
    type="button"
    onClick={() => setActiveView(item.view)}
    className={`rounded-[28px] border p-5 text-left transition-all duration-200 hover:-translate-y-1 ${activeView === item.view ? 'border-primary/30 bg-primary/10 shadow-[0_16px_36px_rgba(230,126,34,0.08)]' : 'border-border-subtle bg-[#161616]/90'}`}
    data-testid={`neuralia-os-workbench-card-${item.id}`}
  >
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Tool / plugin view</p>
        <h4 className="mt-3 text-xl font-semibold text-text-heading">{item.title}</h4>
      </div>
      <span className={`rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.16em] ${STATUS_STYLES[item.status] || STATUS_STYLES.pending}`} data-testid={`neuralia-os-workbench-status-${item.id}`}>
        {item.status}
      </span>
    </div>
    <p className="mt-3 text-sm text-text-body" data-testid={`neuralia-os-workbench-summary-${item.id}`}>{item.summary}</p>
    <div className="mt-4 flex items-end justify-between gap-3">
      <div>
        <p className="text-2xl font-semibold text-text-heading" data-testid={`neuralia-os-workbench-count-${item.id}`}>{item.count}</p>
        <p className="text-xs text-text-muted">{item.count_label}</p>
      </div>
      <div className="text-right text-xs text-text-muted" data-testid={`neuralia-os-workbench-next-${item.id}`}>{item.next_step}</div>
    </div>
  </button>
);

const SectionCard = ({ title, helper, testId, children, action }) => (
  <div className="glass-card tracing-panel p-6" data-testid={testId}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Módulo interno</p>
        <h4 className="mt-2 text-lg font-semibold text-text-heading">{title}</h4>
        {helper && <p className="mt-2 text-sm text-text-body">{helper}</p>}
      </div>
      {action}
    </div>
    <div className="mt-5">{children}</div>
  </div>
);

export default function NeuraliaOSHub({ authFetch }) {
  const [hub, setHub] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [lastPacket, setLastPacket] = useState(null);
  const [activeView, setActiveView] = useState('overview');
  const [studio, setStudio] = useState({
    instruction: '',
    context: '',
    requested_channels: 'web,whatsapp,linkedin',
    autonomy_mode: 'hybrid_limits',
  });

  const loadHub = useCallback(async () => {
    try {
      setRefreshing(true);
      const res = await authFetch(`${API}/admin/neuralia-os-hub`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo cargar Neuralia OS 369');
      setHub(data);
    } catch (error) {
      toast.error(error.message || 'No se pudo cargar Neuralia OS 369');
    } finally {
      setRefreshing(false);
    }
  }, [authFetch]);

  useEffect(() => {
    loadHub();
  }, [loadHub]);

  const translateInstruction = async (event) => {
    event.preventDefault();
    try {
      setTranslating(true);
      const payload = {
        instruction: studio.instruction,
        context: studio.context,
        requested_channels: studio.requested_channels.split(',').map((item) => item.trim()).filter(Boolean),
        autonomy_mode: studio.autonomy_mode,
      };
      const res = await authFetch(`${API}/admin/neuralia-os-hub/nip-translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo traducir la orden al protocolo NIP');
      setLastPacket(data.packet);
      toast.success('Orden traducida a NIP');
      loadHub();
      setActiveView('protocol');
    } catch (error) {
      toast.error(error.message || 'No se pudo traducir la orden al protocolo NIP');
    } finally {
      setTranslating(false);
    }
  };

  const launchHeadline = useMemo(() => {
    if (!hub?.campaign_go_live) return 'Sin datos de lanzamiento';
    return `Score de salida · ${hub.campaign_go_live.score}%`;
  }, [hub]);

  if (!hub) {
    return <div className="glass-card p-8 text-center text-text-muted" data-testid="neuralia-os-loading">Cargando Neuralia OS 369...</div>;
  }

  return (
    <div className="space-y-6" data-testid="neuralia-os-hub">
      <div className="glass-card tracing-panel overflow-hidden" data-testid="neuralia-os-hero-card">
        <div className="grid gap-6 p-6 xl:grid-cols-[1.08fr_0.92fr] xl:p-8">
          <div>
            <p className="editorial-kicker mb-3">Neuralia OS 369</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Diagnóstico aplicado · workbench interno inspirado en Claude</p>
            <h3 className="mt-4 max-w-4xl text-3xl font-semibold text-text-heading">{hub.sales_positioning.headline}</h3>
            <p className="mt-4 max-w-3xl text-lg text-text-body">{hub.identity.short_description}</p>
            <p className="mt-4 max-w-3xl text-sm text-text-muted">Antes había mucha potencia, pero la lectura era demasiado lineal. Ahora el OS se organiza como un workbench de herramientas: más modular, más visible y con foco ejecutivo real.</p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="neuralia-os-diagnosis-chips">
              <span className="insight-chip">Inbox ejecutivo</span>
              <span className="insight-chip">Views por módulo</span>
              <span className="insight-chip">NIP más accesible</span>
              <span className="insight-chip">Go-live visible</span>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <MetricCard label="Órdenes" value={hub.metrics.orders} helper="capas ejecutivas activas" testId="neuralia-os-metric-orders" />
            <MetricCard label="Tareas" value={hub.metrics.tasks} helper="tareas coordinadas" testId="neuralia-os-metric-tasks" />
            <MetricCard label="Integraciones listas" value={hub.metrics.integrations_ready} helper="canales listos o preparables" testId="neuralia-os-metric-integrations" />
            <MetricCard label="NIP packets" value={hub.metrics.nip_packets_visible} helper="paquetes visibles en monitor" testId="neuralia-os-metric-nip" />
          </div>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-3" data-testid="neuralia-os-workbench-grid">
        {(hub.plugin_workbench || []).map((item) => (
          <WorkbenchCard key={item.id} item={item} activeView={activeView} setActiveView={setActiveView} />
        ))}
      </div>

      <div className="glass-card tracing-panel p-6" data-testid="neuralia-os-view-switcher-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Navigation layer</p>
            <h4 className="mt-2 text-xl font-semibold text-text-heading">Elige la vista de trabajo y entra directo al bloque que importa</h4>
          </div>
          <button onClick={loadHub} className="btn-secondary" disabled={refreshing} data-testid="neuralia-os-refresh-button">
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Refrescar
          </button>
        </div>

        <Tabs value={activeView} onValueChange={setActiveView} className="mt-5" data-testid="neuralia-os-tabs-root">
          <TabsList className="flex h-auto w-full flex-wrap justify-start gap-2 rounded-[20px] border border-border-subtle bg-background/40 p-2" data-testid="neuralia-os-tabs-list">
            <TabsTrigger value="overview" className="rounded-full px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-black" data-testid="neuralia-os-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="protocol" className="rounded-full px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-black" data-testid="neuralia-os-tab-protocol">Protocolos</TabsTrigger>
            <TabsTrigger value="launch" className="rounded-full px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-black" data-testid="neuralia-os-tab-launch">Launch</TabsTrigger>
            <TabsTrigger value="growth" className="rounded-full px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-black" data-testid="neuralia-os-tab-growth">Growth</TabsTrigger>
            <TabsTrigger value="compliance" className="rounded-full px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-black" data-testid="neuralia-os-tab-compliance">Compliance</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-5 space-y-6" data-testid="neuralia-os-view-overview">
            {hub.mcp_blueprint && (
              <SectionCard title="Aynara + Neuralia MCP OS" helper="La nueva capa maestra que unifica conversación, agentes, memoria y herramientas externas." testId="neuralia-os-mcp-blueprint-card">
                <div className="grid gap-4 xl:grid-cols-[0.9fr_1.1fr]">
                  <div className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid="neuralia-os-aynara-summary-card">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Asistente central</p>
                    <p className="mt-2 text-2xl font-semibold text-text-heading">{hub.mcp_blueprint.identity.assistant_name}</p>
                    <p className="mt-2 text-sm text-text-body">{hub.mcp_blueprint.identity.mission}</p>
                    <div className="mt-4 flex flex-wrap gap-2">{hub.mcp_blueprint.identity.tone.map((item) => <span key={item} className="rounded-full border border-primary/20 bg-primary/10 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-primary">{item}</span>)}</div>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="neuralia-os-mcp-layers-grid">
                    {hub.mcp_blueprint.layers.map((item) => (
                      <div key={item.key} className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`neuralia-os-mcp-layer-${item.key}`}>
                        <p className="text-sm font-semibold text-text-heading">{item.name}</p>
                        <p className="mt-2 text-xs text-text-body">{item.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4" data-testid="neuralia-os-mcp-modules-grid">
                  {hub.mcp_blueprint.core_modules.map((item) => (
                    <div key={item.key} className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid={`neuralia-os-mcp-module-${item.key}`}>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{item.category}</p>
                      <p className="mt-2 text-sm font-semibold text-text-heading">{item.name}</p>
                      <p className="mt-2 text-xs text-text-body">{item.headline}</p>
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}

            <div className="grid gap-6 xl:grid-cols-[1.12fr_0.88fr]">
              <SectionCard title="Canales fase 1" helper="Los primeros canales unificados del sistema, con estado real y siguiente paso visible." testId="neuralia-os-channels-card">
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {hub.channels.map((channel) => (
                    <div key={channel.key} className="rounded-[24px] border border-border-subtle bg-background/55 p-4" data-testid={`neuralia-os-channel-${channel.key}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{channel.phase}</p>
                          <h5 className="mt-2 text-lg font-semibold text-text-heading">{channel.name}</h5>
                        </div>
                        <span className={`rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.16em] ${STATUS_STYLES[channel.readiness] || 'border border-border-subtle text-text-muted'}`}>{channel.readiness}</span>
                      </div>
                      <p className="mt-3 text-sm text-text-body">{channel.role}</p>
                      {channel.next_step && <p className="mt-3 text-xs text-text-muted">{channel.next_step}</p>}
                      {!!channel.missing_credentials?.length && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {channel.missing_credentials.map((item) => (
                            <span key={item} className="rounded-full border border-amber-300/20 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-amber-100">{item}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </SectionCard>

              <SectionCard title="War room ejecutivo" helper="Prioridades, pasos guiados y el último paquete NIP dentro de la misma vista." testId="neuralia-os-war-room-card">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid="neuralia-os-guided-steps-card">
                    <p className="mb-3 text-[10px] uppercase tracking-[0.18em] text-text-muted">Pasos guiados</p>
                    <div className="space-y-3">
                      {hub.executive_flow.guided_steps.map((item) => (
                        <div key={item.id}>
                          <p className="text-sm font-semibold text-text-heading">{item.title}</p>
                          <p className="mt-1 text-xs text-text-muted">{item.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid="neuralia-os-action-inbox-card">
                    <p className="mb-3 text-[10px] uppercase tracking-[0.18em] text-text-muted">Inbox ejecutivo</p>
                    <div className="space-y-3">
                      {hub.executive_flow.action_inbox.map((item) => (
                        <div key={item.id}>
                          <p className="text-sm font-semibold text-text-heading">{item.title}</p>
                          <p className="mt-1 text-xs text-text-muted">{item.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-5 rounded-[22px] border border-primary/15 bg-primary/5 p-4" data-testid="neuralia-os-last-packet-card">
                  <p className="mb-3 text-[10px] uppercase tracking-[0.18em] text-text-muted">Último paquete NIP</p>
                  {lastPacket ? (
                    <div className="space-y-2 text-sm text-text-body">
                      <p><strong className="text-text-heading">INTENT:</strong> {lastPacket.intent}</p>
                      <p><strong className="text-text-heading">CONTEXT:</strong> {lastPacket.context}</p>
                      <p><strong className="text-text-heading">AGENTS:</strong> {(lastPacket.agents || []).join(', ')}</p>
                      <p><strong className="text-text-heading">ACTION:</strong> {lastPacket.action}</p>
                      <p><strong className="text-text-heading">CHECK:</strong> {lastPacket.check}</p>
                      <p><strong className="text-text-heading">REPORT:</strong> {lastPacket.report}</p>
                    </div>
                  ) : (
                    <p className="text-sm text-text-muted">Todavía no has traducido una orden desde NIP Studio.</p>
                  )}
                </div>
              </SectionCard>
            </div>
          </TabsContent>

          <TabsContent value="protocol" className="mt-5 space-y-6" data-testid="neuralia-os-view-protocol">
            <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
              <SectionCard title="Neuralia Interlink Protocol · NIP" helper="Campo por campo, aquí se ve el contrato operativo del sistema." testId="neuralia-os-protocol-card">
                <div className="space-y-3">
                  {hub.nip_protocol.map((item) => (
                    <div key={item.field} className="rounded-[20px] border border-border-subtle bg-background/55 px-4 py-3" data-testid={`neuralia-os-nip-field-${item.field}`}>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{item.field}</p>
                      <p className="mt-2 text-sm text-text-body">{item.description}</p>
                    </div>
                  ))}
                </div>
              </SectionCard>

              <SectionCard title="NIP Studio" helper="Escribes una orden humana y Neuralia la convierte en un paquete ejecutable, trazable y supervisable." testId="neuralia-os-nip-studio">
                <form onSubmit={translateInstruction} className="space-y-4">
                  <textarea className="input-kinesthetic resize-none" rows={4} placeholder="Escribe una orden humana..." value={studio.instruction} onChange={(e) => setStudio((prev) => ({ ...prev, instruction: e.target.value }))} data-testid="nip-studio-instruction-input" />
                  <textarea className="input-kinesthetic resize-none" rows={3} placeholder="Contexto adicional" value={studio.context} onChange={(e) => setStudio((prev) => ({ ...prev, context: e.target.value }))} data-testid="nip-studio-context-input" />
                  <input className="input-kinesthetic" placeholder="Canales (coma)" value={studio.requested_channels} onChange={(e) => setStudio((prev) => ({ ...prev, requested_channels: e.target.value }))} data-testid="nip-studio-channels-input" />
                  <select className="input-kinesthetic" value={studio.autonomy_mode} onChange={(e) => setStudio((prev) => ({ ...prev, autonomy_mode: e.target.value }))} data-testid="nip-studio-autonomy-select">
                    <option value="manual_guarded">manual_guarded</option>
                    <option value="hybrid_limits">hybrid_limits</option>
                    <option value="client_tunable">client_tunable</option>
                  </select>
                  <button type="submit" className="btn-primary" disabled={translating} data-testid="nip-studio-submit-button">
                    <ArrowRightLeft className="h-4 w-4" /> {translating ? 'Traduciendo...' : 'Traducir a NIP'}
                  </button>
                </form>
              </SectionCard>
            </div>

            <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
              <SectionCard title="Supervisión humana" helper="Modos de control para no perder gobierno sobre el sistema." testId="neuralia-os-supervision-card">
                <div className="space-y-3">
                  {hub.supervision_modes.map((item) => (
                    <div key={item.id} className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`neuralia-os-supervision-${item.id}`}>
                      <p className="font-semibold text-text-heading">{item.name}</p>
                      <p className="mt-2 text-sm text-text-body">{item.description}</p>
                    </div>
                  ))}
                </div>
              </SectionCard>

              <SectionCard title="NIP Monitor" helper="Órdenes, briefings y acciones ya normalizadas bajo el mismo lenguaje." testId="neuralia-os-monitor-card">
                <div className="space-y-3">
                  {hub.nip_monitor.map((item) => (
                    <div key={item.id} className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid={`neuralia-os-packet-${item.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{item.source}</p>
                          <h5 className="mt-2 text-sm font-semibold text-text-heading">{item.intent}</h5>
                        </div>
                        <span className={`rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.16em] ${item.check === 'humano' ? 'border border-amber-400/20 bg-amber-500/10 text-amber-200' : 'border border-primary/20 bg-primary/10 text-primary'}`}>{item.check}</span>
                      </div>
                      <p className="mt-3 text-sm text-text-body">{item.context}</p>
                      <div className="mt-3 flex flex-wrap gap-2">{(item.agents || []).map((agent) => <span key={agent} className="rounded-full border border-primary/20 bg-primary/10 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-primary">{agent}</span>)}</div>
                      <div className="mt-3 grid gap-2 text-xs text-text-muted md:grid-cols-2">
                        <p><strong className="text-text-heading">ACTION:</strong> {item.action}</p>
                        <p><strong className="text-text-heading">REPORT:</strong> {item.report}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>
            </div>
          </TabsContent>

          <TabsContent value="launch" className="mt-5 space-y-6" data-testid="neuralia-os-view-launch">
            <SectionCard title="Radar final de lanzamiento" helper="Qué está listo y qué falta para operar con una empresa piloto real." testId="neuralia-os-go-live-card">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="max-w-3xl">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted" data-testid="neuralia-os-go-live-kicker">Launch control</p>
                  <h4 className="mt-2 text-xl font-semibold text-text-heading" data-testid="neuralia-os-go-live-title">{launchHeadline}</h4>
                  <p className="mt-3 text-sm text-text-body" data-testid="neuralia-os-go-live-helper">Aquí se cruzan dominio, email, analítica, canales comerciales e IA para ver estado real, no una sensación optimista.</p>
                </div>
                <span className="rounded-full border border-primary/20 bg-primary/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-primary" data-testid="neuralia-os-go-live-score">
                  Score · {hub.campaign_go_live.score}%
                </span>
              </div>

              <div className="mt-6 grid gap-4 md:grid-cols-3">
                <MetricCard label="Listos" value={hub.campaign_go_live.ready_count} helper="bloques ya preparados" testId="neuralia-os-go-live-ready" />
                <MetricCard label="Pendientes" value={hub.campaign_go_live.pending_count} helper="configurados a falta de validación" testId="neuralia-os-go-live-pending" />
                <MetricCard label="Bloqueados" value={hub.campaign_go_live.blocked_count} helper="dependen de credenciales o decisión" testId="neuralia-os-go-live-blocked" />
              </div>

              <div className="mt-6 grid gap-4 xl:grid-cols-[1.12fr_0.88fr]">
                <div className="space-y-3" data-testid="neuralia-os-go-live-items">
                  {hub.campaign_go_live.items.map((item) => {
                    const Icon = item.status === 'ready' || item.status === 'connected' ? CheckCircle2 : item.status === 'pending' ? Activity : AlertTriangle;
                    return (
                      <div key={item.id} className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid={`neuralia-os-go-live-item-${item.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="max-w-3xl">
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4 text-primary" />
                              <p className="text-sm font-semibold text-text-heading" data-testid={`neuralia-os-go-live-item-name-${item.id}`}>{item.name}</p>
                            </div>
                            <p className="mt-2 text-sm text-text-body" data-testid={`neuralia-os-go-live-item-helper-${item.id}`}>{item.helper}</p>
                            <p className="mt-2 text-xs text-text-muted" data-testid={`neuralia-os-go-live-item-next-${item.id}`}>{item.next_step}</p>
                          </div>
                          <span className={`rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.16em] ${GO_LIVE_STATUS_STYLES[item.status] || 'border border-border-subtle text-text-muted'}`} data-testid={`neuralia-os-go-live-item-status-${item.id}`}>{item.status}</span>
                        </div>
                        {!!item.missing?.length && (
                          <div className="mt-3 flex flex-wrap gap-2" data-testid={`neuralia-os-go-live-item-missing-${item.id}`}>
                            {item.missing.map((value) => (
                              <span key={value} className="rounded-full border border-border-subtle px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-text-muted">{value}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="space-y-4">
                  <div className="rounded-[24px] border border-border-subtle bg-card/40 p-5" data-testid="neuralia-os-go-live-meta-card">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Base pública</p>
                    <p className="mt-3 text-sm text-text-body" data-testid="neuralia-os-go-live-site-url">Web: {hub.campaign_go_live.site_url || 'no detectada'}</p>
                    <p className="mt-2 text-sm text-text-body" data-testid="neuralia-os-go-live-sender-email">Remitente: {hub.campaign_go_live.sender_email || 'sin configurar'}</p>
                  </div>

                  <div className="rounded-[24px] border border-border-subtle bg-card/40 p-5" data-testid="neuralia-os-go-live-next-actions-card">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Secuencia recomendada</p>
                    <div className="mt-4 space-y-3" data-testid="neuralia-os-go-live-next-actions-list">
                      {hub.campaign_go_live.next_actions.map((step, index) => (
                        <div key={step} className="flex items-start gap-3 text-sm text-text-body" data-testid={`neuralia-os-go-live-next-action-${index}`}>
                          <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">{index + 1}</span>
                          <span>{step}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </SectionCard>

            <div className="grid gap-6 xl:grid-cols-[0.92fr_1.08fr]">
              <SectionCard title="Checklist guiado de lanzamiento" helper="Convierte el “qué te falta” en una lista operativa accionable." testId="neuralia-os-launch-checklist-card">
                <div className="space-y-3">
                  {hub.launch_readiness.map((item) => (
                    <div key={item.id} className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid={`neuralia-os-launch-item-${item.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-text-heading">{item.name}</p>
                          <p className="mt-1 text-xs text-text-muted">{item.next_step}</p>
                        </div>
                        <span className={`rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.16em] ${STATUS_STYLES[item.status] || 'border border-border-subtle text-text-muted'}`}>{item.status}</span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">{(item.missing_credentials || item.required_items).map((value) => <span key={value} className="rounded-full border border-border-subtle px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-text-muted">{value}</span>)}</div>
                    </div>
                  ))}
                </div>
              </SectionCard>

              <SectionCard title="Seguridad, auditoría y valor" helper="La ventaja no está solo en conectar canales; está en coordinar todo con lenguaje propio, seguridad fuerte y trazabilidad real." testId="neuralia-os-security-card">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid="neuralia-os-security-score">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Security score</p>
                    <p className="mt-2 text-3xl font-bold text-text-heading">{hub.security.security_score}</p>
                    <p className="mt-2 text-sm text-text-body">Base segura para seguir escalando el sistema nervioso empresarial.</p>
                  </div>
                  <div className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid="neuralia-os-credentials-card">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Credenciales necesarias</p>
                    <div className="mt-3 space-y-2 text-sm text-text-body">
                      {hub.required_credentials_summary.map((item) => <p key={item.provider_key}>• {item.name}: {item.status}</p>)}
                    </div>
                  </div>
                </div>
              </SectionCard>
            </div>
          </TabsContent>

          <TabsContent value="growth" className="mt-5 space-y-6" data-testid="neuralia-os-view-growth">
            <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]" data-testid="neuralia-os-growth-system-grid">
              <SectionCard title={hub.growth_os.core_positioning.headline} helper={hub.growth_os.core_positioning.sales_adaptation} testId="neuralia-os-growth-positioning-card">
                <p className="text-sm text-text-muted">{hub.growth_os.core_positioning.promise}</p>
                <div className="mt-5 space-y-3">
                  {hub.growth_os.sales_system.principles.map((item) => (
                    <div key={item} className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid="neuralia-os-growth-principle-item">
                      <p className="text-sm text-text-body">{item}</p>
                    </div>
                  ))}
                </div>
              </SectionCard>

              <div className="space-y-6">
                <SectionCard title="SEO + Discoverability + Navegadores" helper="Crecimiento técnico que ayuda a vender y posicionar." testId="neuralia-os-growth-seo-card">
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="rounded-[20px] border border-border-subtle bg-background/55 p-4">
                      <p className="text-sm font-semibold text-text-heading">Google</p>
                      <ul className="mt-3 space-y-2 text-sm text-text-body">{hub.growth_os.seo_system.google_growth.map((item) => <li key={item}>• {item}</li>)}</ul>
                    </div>
                    <div className="rounded-[20px] border border-border-subtle bg-background/55 p-4">
                      <p className="text-sm font-semibold text-text-heading">IA / LLM discoverability</p>
                      <ul className="mt-3 space-y-2 text-sm text-text-body">{hub.growth_os.seo_system.ai_discoverability.map((item) => <li key={item}>• {item}</li>)}</ul>
                    </div>
                    <div className="rounded-[20px] border border-border-subtle bg-background/55 p-4">
                      <p className="text-sm font-semibold text-text-heading">Chrome / Firefox / Edge</p>
                      <ul className="mt-3 space-y-2 text-sm text-text-body">{hub.growth_os.seo_system.browser_experience.map((item) => <li key={item}>• {item}</li>)}</ul>
                    </div>
                  </div>
                </SectionCard>

                <SectionCard title="Ventas, contenidos y automatización" helper="Los tres frentes que convierten el OS en una máquina comercial útil." testId="neuralia-os-growth-content-card">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="rounded-[20px] border border-border-subtle bg-background/55 p-4">
                      <p className="text-sm font-semibold text-text-heading">Pilares de contenido</p>
                      <ul className="mt-3 space-y-2 text-sm text-text-body">{hub.growth_os.content_system.pillars.map((item) => <li key={item}>• {item}</li>)}</ul>
                    </div>
                    <div className="rounded-[20px] border border-border-subtle bg-background/55 p-4">
                      <p className="text-sm font-semibold text-text-heading">Prioridades de automatización</p>
                      <ul className="mt-3 space-y-2 text-sm text-text-body">{hub.growth_os.automation_priorities.map((item) => <li key={item}>• {item}</li>)}</ul>
                    </div>
                  </div>
                </SectionCard>
              </div>
            </div>

            {hub.growth_os.llops_framework && (
              <SectionCard title={hub.growth_os.llops_framework.headline} helper="Research, mercado, cierres y auditoría dentro del mismo sistema de growth." testId="neuralia-os-llops-card">
                <div className="space-y-3">
                  {hub.growth_os.llops_framework.rules.map((item) => (
                    <div key={item} className="rounded-[20px] border border-border-subtle bg-background/55 p-4">
                      <p className="text-sm text-text-body">{item}</p>
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}

            {(hub.growth_os.reference_integration_matrix || []).length > 0 && (
              <SectionCard title="Matriz de referencias y competencia" helper="Qué integrar, qué mejorar y qué no copiar del mercado." testId="neuralia-os-reference-matrix-card">
                <div className="space-y-4">
                  {hub.growth_os.reference_integration_matrix.map((item) => (
                    <div key={item.reference} className="rounded-[22px] border border-border-subtle bg-background/55 p-5" data-testid={`neuralia-os-reference-item-${item.reference}`}>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-text-heading">{item.reference}</p>
                          <p className="mt-2 text-sm text-text-body">{item.integration}</p>
                        </div>
                        <div className="flex flex-wrap gap-2 text-[11px]">
                          <span className="rounded-full border border-primary/20 bg-primary/10 px-2 py-1 text-primary">Valor: {item.value}</span>
                          <span className="rounded-full border border-secondary/20 bg-secondary/10 px-2 py-1 text-secondary">Impacto: {item.impact}</span>
                          <span className="rounded-full border border-border-subtle px-2 py-1 text-text-muted">Dificultad: {item.difficulty}</span>
                          <span className="rounded-full border border-amber-300/20 bg-amber-500/10 px-2 py-1 text-amber-200">{item.priority}</span>
                        </div>
                      </div>
                      <div className="mt-4 grid gap-4 text-sm md:grid-cols-3">
                        <div className="rounded-[18px] border border-border-subtle bg-card/40 p-4">
                          <p className="font-medium text-text-heading">Copiar conceptualmente</p>
                          <p className="mt-2 text-text-body">{item.what_to_copy}</p>
                        </div>
                        <div className="rounded-[18px] border border-border-subtle bg-card/40 p-4">
                          <p className="font-medium text-text-heading">Mejorar</p>
                          <p className="mt-2 text-text-body">{item.what_to_improve}</p>
                        </div>
                        <div className="rounded-[18px] border border-border-subtle bg-card/40 p-4">
                          <p className="font-medium text-text-heading">No copiar</p>
                          <p className="mt-2 text-text-body">{item.what_not_to_copy}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}
          </TabsContent>

          <TabsContent value="compliance" className="mt-5 space-y-6" data-testid="neuralia-os-view-compliance">
            <div className="grid gap-6 xl:grid-cols-[0.82fr_1.18fr]">
              <SectionCard title="Filosofía operativa" helper="Los principios que ordenan cómo se usa y gobierna el sistema." testId="neuralia-os-philosophy-card">
                <div className="space-y-3">
                  {hub.philosophy.map((item) => (
                    <div key={item.title} className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid={`neuralia-os-philosophy-${item.title}`}>
                      <p className="font-semibold text-text-heading">{item.title}</p>
                      <p className="mt-2 text-sm text-text-body">{item.description}</p>
                    </div>
                  ))}
                </div>
              </SectionCard>

              <SectionCard title="AI compliance readiness" helper="Transparencia, supervisión humana y límites antes de escalar." testId="neuralia-os-ai-compliance-card">
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="rounded-[20px] border border-border-subtle bg-background/55 p-4">
                    <p className="text-sm font-semibold text-text-heading">AI Act UE</p>
                    <p className="mt-2 text-sm text-text-body">Preparado el enfoque de transparencia, supervisión humana y límites del uso de IA en servicios y plataforma.</p>
                  </div>
                  <div className="rounded-[20px] border border-border-subtle bg-background/55 p-4">
                    <p className="text-sm font-semibold text-text-heading">RGPD + LOPDGDD</p>
                    <p className="mt-2 text-sm text-text-body">Contratos y sistema interno orientados a tratamiento mínimo, seguridad razonable y reparto de responsabilidades.</p>
                  </div>
                  <div className="rounded-[20px] border border-border-subtle bg-background/55 p-4">
                    <p className="text-sm font-semibold text-text-heading">AESIA / España</p>
                    <p className="mt-2 text-sm text-text-body">Checklist interno preparado para revisar transparencia, logs, supervisión y documentación final.</p>
                  </div>
                </div>

                <div className="mt-5 rounded-[22px] border border-amber-400/20 bg-amber-500/10 p-5" data-testid="neuralia-os-ai-act-warning-card">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-amber-200">AI Act UE · control operativo</p>
                  <p className="mt-3 text-sm text-text-body">Neuralia369 debe evitar usos prohibidos, informar cuándo hay IA involucrada, mantener supervisión humana en decisiones sensibles y documentar límites, revisión y trazabilidad.</p>
                </div>

                <div className="mt-5 grid gap-4 xl:grid-cols-[0.95fr_1.05fr]">
                  <div className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid="neuralia-os-ai-act-prohibited-list">
                    <p className="text-sm font-semibold text-text-heading">Prácticas prohibidas que bloqueamos</p>
                    <div className="mt-4 space-y-3">
                      {(hub.eu_ai_act?.prohibited_practices || []).map((item) => (
                        <div key={item.id} className="rounded-[18px] border border-rose-400/15 bg-rose-500/5 p-3" data-testid={`neuralia-os-ai-act-prohibited-${item.id}`}>
                          <p className="text-sm font-medium text-text-heading">{item.title}</p>
                          <p className="mt-1 text-xs text-text-muted">{item.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid="neuralia-os-ai-act-checklist">
                    <p className="text-sm font-semibold text-text-heading">Checklist operativo UE</p>
                    <div className="mt-4 space-y-3">
                      {(hub.eu_ai_act?.readiness_checklist || []).map((item) => (
                        <div key={item.id} className="rounded-[18px] border border-border-subtle bg-card/40 p-3" data-testid={`neuralia-os-ai-act-check-${item.id}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-sm font-medium text-text-heading">{item.label}</p>
                              <p className="mt-1 text-xs text-text-muted">{item.detail}</p>
                            </div>
                            <span className={`rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.16em] ${item.status === 'active' ? 'border border-primary/20 bg-primary/10 text-primary' : 'border border-amber-400/20 bg-amber-500/10 text-amber-200'}`}>{item.status}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </SectionCard>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}